import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "../components/ui/tabs";
import { Alert, AlertDescription } from "../components/ui/alert";
import User from "../database/User";
import MfaVerify from "./MfaVerify";

export default function Login() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [activeTab, setActiveTab] = useState("login");

    // MFA state
    const [mfaChallenge, setMfaChallenge] = useState(null);

    // Check if user is already authenticated
    useEffect(() => {
        if (User.isAuthenticated) {
            navigate("/");
        }
    }, [navigate]);

    // Login form state
    const [loginForm, setLoginForm] = useState({
        emailOrUsername: "",
        password: "",
    });

    // Signup form state
    const [signupForm, setSignupForm] = useState({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        firstName: "",
        lastName: "",
    });

    // Password reset form state
    const [resetForm, setResetForm] = useState({
        emailOrUsername: "",
    });

    // Check if signup is allowed
    const allowSelfSignup = window.config?.allowSelfSignup || false;
    const ssoEnabled = User.isSSOEnabled;

    // Handle SSO error from callback
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const ssoError = urlParams.get("x_sso_error");
        if (ssoError) setError(ssoError);
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");
        setLoading(true);

        try {
            const result = await User.login(
                loginForm.emailOrUsername,
                loginForm.password
            );
            if (result.mfaRequired) {
                setMfaChallenge({
                    token: result.mfaChallengeToken,
                    email: result.email,
                });
                return;
            }
            navigate("/");
        } catch (err) {
            console.error("Login error:", err);
            setError(
                err.message ||
                    "An error occurred during login. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        // Validate passwords match
        if (signupForm.password !== signupForm.confirmPassword) {
            setError("Passwords do not match");
            return;
        }

        // Validate password strength
        if (signupForm.password.length < 7 || !/[A-Z]/.test(signupForm.password) || !/[0-9]/.test(signupForm.password)) {
            setError("Password must be at least 7 characters and contain at least one uppercase letter and one number");
            return;
        }

        setLoading(true);

        try {
            await User.signup({
                username: signupForm.username,
                email: signupForm.email,
                password: signupForm.password,
                firstName: signupForm.firstName,
                lastName: signupForm.lastName,
            });
            navigate("/");
        } catch (err) {
            console.error("Signup error:", err);
            setError(
                err.message ||
                    "An error occurred during signup. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const updateLoginForm = (field, value) => {
        setLoginForm((prev) => ({ ...prev, [field]: value }));
    };

    const updateSignupForm = (field, value) => {
        setSignupForm((prev) => ({ ...prev, [field]: value }));
    };

    const handlePasswordReset = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");
        setLoading(true);

        try {
            const response = await User.requestPasswordReset(
                resetForm.emailOrUsername
            );
            setSuccess(
                response.data?.message ||
                    "If a user with that email exists, a password reset link has been sent."
            );
            setResetForm({ emailOrUsername: "" });
        } catch (err) {
            console.error("Password reset error:", err);
            setError(
                err.message ||
                    "An error occurred. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const updateResetForm = (field, value) => {
        setResetForm((prev) => ({ ...prev, [field]: value }));
    };

    if (mfaChallenge) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
                <div className="w-full max-w-md">
                    <MfaVerify
                        mfaChallengeToken={mfaChallenge.token}
                        email={mfaChallenge.email}
                        onBack={() => setMfaChallenge(null)}
                        onSuccess={() => navigate("/")}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
            <div className="w-full max-w-md">
                <Tabs
                    value={activeTab}
                    onValueChange={setActiveTab}
                    className="w-full"
                >
                    <TabsList
                        className={`grid w-full ${
                            allowSelfSignup ? "grid-cols-3" : "grid-cols-2"
                        }`}
                    >
                        <TabsTrigger value="login">Login</TabsTrigger>
                        {allowSelfSignup && (
                            <TabsTrigger value="signup">Sign Up</TabsTrigger>
                        )}
                        <TabsTrigger value="reset">Reset Password</TabsTrigger>
                    </TabsList>

                    <TabsContent value="login">
                        <Card>
                            <CardHeader>
                                <CardTitle>Welcome Back</CardTitle>
                                <CardDescription>
                                    Enter your credentials to access your
                                    account
                                </CardDescription>
                            </CardHeader>
                            <form onSubmit={handleLogin}>
                                <CardContent className="space-y-4">
                                    {error && (
                                        <Alert variant="destructive">
                                            <AlertDescription>
                                                {error}
                                            </AlertDescription>
                                        </Alert>
                                    )}

                                    <div className="space-y-2">
                                        <Label htmlFor="emailOrUsername">
                                            Email or Username
                                        </Label>
                                        <Input
                                            id="emailOrUsername"
                                            type="text"
                                            placeholder="Enter your email or username"
                                            value={loginForm.emailOrUsername}
                                            onChange={(e) =>
                                                updateLoginForm(
                                                    "emailOrUsername",
                                                    e.target.value
                                                )
                                            }
                                            required
                                            disabled={loading}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="password">
                                            Password
                                        </Label>
                                        <Input
                                            id="password"
                                            type="password"
                                            placeholder="Enter your password"
                                            value={loginForm.password}
                                            onChange={(e) =>
                                                updateLoginForm(
                                                    "password",
                                                    e.target.value
                                                )
                                            }
                                            required
                                            disabled={loading}
                                        />
                                    </div>
                                </CardContent>
                                <CardFooter className="flex flex-col space-y-2">
                                    <Button
                                        type="submit"
                                        className="w-full"
                                        disabled={loading}
                                    >
                                        {loading ? "Logging in..." : "Login"}
                                    </Button>
                                    {ssoEnabled && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="w-full"
                                            disabled={loading}
                                            onClick={() => User.ssoLogin()}
                                        >
                                            Login with SSO
                                        </Button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab("reset")}
                                        className="text-sm text-muted-foreground hover:text-primary underline"
                                    >
                                        Forgot your password?
                                    </button>
                                </CardFooter>
                            </form>
                        </Card>
                    </TabsContent>

                    {allowSelfSignup && (
                        <TabsContent value="signup">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Create Account</CardTitle>
                                    <CardDescription>
                                        Fill in the information below to create
                                        your account
                                    </CardDescription>
                                </CardHeader>
                                <form onSubmit={handleSignup}>
                                    <CardContent className="space-y-4">
                                        {error && (
                                            <Alert variant="destructive">
                                                <AlertDescription>
                                                    {error}
                                                </AlertDescription>
                                            </Alert>
                                        )}

                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="firstName">
                                                    First Name
                                                </Label>
                                                <Input
                                                    id="firstName"
                                                    type="text"
                                                    placeholder="First name"
                                                    value={signupForm.firstName}
                                                    onChange={(e) =>
                                                        updateSignupForm(
                                                            "firstName",
                                                            e.target.value
                                                        )
                                                    }
                                                    required
                                                    disabled={loading}
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="lastName">
                                                    Last Name
                                                </Label>
                                                <Input
                                                    id="lastName"
                                                    type="text"
                                                    placeholder="Last name"
                                                    value={signupForm.lastName}
                                                    onChange={(e) =>
                                                        updateSignupForm(
                                                            "lastName",
                                                            e.target.value
                                                        )
                                                    }
                                                    required
                                                    disabled={loading}
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="signupEmail">
                                                Email
                                            </Label>
                                            <Input
                                                id="signupEmail"
                                                type="email"
                                                placeholder="Enter your email"
                                                value={signupForm.email}
                                                onChange={(e) =>
                                                    updateSignupForm(
                                                        "email",
                                                        e.target.value
                                                    )
                                                }
                                                required
                                                disabled={loading}
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="signupPassword">
                                                Password
                                            </Label>
                                            <Input
                                                id="signupPassword"
                                                type="password"
                                                placeholder="Min. 7 chars, 1 uppercase, 1 number"
                                                value={signupForm.password}
                                                onChange={(e) =>
                                                    updateSignupForm(
                                                        "password",
                                                        e.target.value
                                                    )
                                                }
                                                required
                                                disabled={loading}
                                                minLength={7}
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="confirmPassword">
                                                Confirm Password
                                            </Label>
                                            <Input
                                                id="confirmPassword"
                                                type="password"
                                                placeholder="Confirm your password"
                                                value={
                                                    signupForm.confirmPassword
                                                }
                                                onChange={(e) =>
                                                    updateSignupForm(
                                                        "confirmPassword",
                                                        e.target.value
                                                    )
                                                }
                                                required
                                                disabled={loading}
                                            />
                                        </div>
                                    </CardContent>
                                    <CardFooter>
                                        <Button
                                            type="submit"
                                            className="w-full"
                                            disabled={loading}
                                        >
                                            {loading
                                                ? "Creating account..."
                                                : "Sign Up"}
                                        </Button>
                                    </CardFooter>
                                </form>
                            </Card>
                        </TabsContent>
                    )}

                    <TabsContent value="reset">
                        <Card>
                            <CardHeader>
                                <CardTitle>Reset Password</CardTitle>
                                <CardDescription>
                                    Enter your email or username to receive a
                                    password reset link
                                </CardDescription>
                            </CardHeader>
                            <form onSubmit={handlePasswordReset}>
                                <CardContent className="space-y-4">
                                    {error && (
                                        <Alert variant="destructive">
                                            <AlertDescription>
                                                {error}
                                            </AlertDescription>
                                        </Alert>
                                    )}

                                    {success && (
                                        <Alert>
                                            <AlertDescription>
                                                {success}
                                            </AlertDescription>
                                        </Alert>
                                    )}

                                    <div className="space-y-2">
                                        <Label htmlFor="resetEmailOrUsername">
                                            Email or Username
                                        </Label>
                                        <Input
                                            id="resetEmailOrUsername"
                                            type="text"
                                            placeholder="Enter your email or username"
                                            value={resetForm.emailOrUsername}
                                            onChange={(e) =>
                                                updateResetForm(
                                                    "emailOrUsername",
                                                    e.target.value
                                                )
                                            }
                                            required
                                            disabled={loading}
                                        />
                                    </div>
                                </CardContent>
                                <CardFooter className="flex flex-col space-y-2">
                                    <Button
                                        type="submit"
                                        className="w-full"
                                        disabled={loading}
                                    >
                                        {loading
                                            ? "Sending..."
                                            : "Send Reset Link"}
                                    </Button>
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab("login")}
                                        className="text-sm text-muted-foreground hover:text-primary underline"
                                    >
                                        Back to Login
                                    </button>
                                </CardFooter>
                            </form>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}
